import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress } from '@mui/material';

/**
 * Route guard that renders children only if the authenticated user holds one of
 * the allowed roles (super_admin always passes, per hasRole). This is cosmetic;
 * the server enforces permissions authoritatively via requireRole middleware.
 *
 * Usage:
 *   <Route element={<RequireRole allowed={['finance']} />}>
 *     <Route path="/finance-review" element={<FinanceReview />} />
 *   </Route>
 */
function RequireRole({ allowed = [], redirectTo = '/' }) {
  const { isAuthenticated, loading, rolesLoading, hasRole } = useAuth();

  // Wait for both the session check and the async role fetch — otherwise an
  // authenticated user can be evaluated against a not-yet-loaded (empty) role
  // set and wrongly redirected.
  if (loading || rolesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasRole(...allowed)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}

export default RequireRole;
